/**
 * PettyCashVoucherPrint — the printable petty cash voucher.
 *
 * Rendered inside MasterReportPreview, same as every other voucher printout. Uses
 * VoucherLetterhead for the company block — see that component for why a voucher must never print
 * the software's own name where the issuing company's belongs.
 */
import { VoucherLetterhead } from '@/components/master/report/VoucherLetterhead';
import type { PettyCashVoucherDto } from '@/types/pettyCashVoucher.types';

const MOVEMENT_LABEL: Record<string, string> = {
  TOPUP: 'Top-up', PAYMENT: 'Payment', RETURN: 'Return',
};

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso; }
}

function fmtNum(n: number, dec = 2): string {
  return n.toLocaleString('en-US', { minimumFractionDigits: dec, maximumFractionDigits: dec });
}

const LabelValue = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div style={{ display: 'flex', gap: '4px', fontSize: '10px', lineHeight: 1.7 }}>
    <span style={{ color: '#6b7280', minWidth: '130px', flexShrink: 0 }}>{label}:</span>
    <span style={{ color: '#111827', fontWeight: 500 }}>{value}</span>
  </div>
);

interface Props {
  voucher: PettyCashVoucherDto;
}

export const PettyCashVoucherPrint = ({ voucher }: Props) => {
  // Only a payment itemises, and only one raised after ML-13 carries lines at all — an older
  // voucher still prints its single account exactly as it always did.
  const hasExpenseLines = (voucher.lines?.length ?? 0) > 0;

  const workflowRows = [
    voucher.submittedBy ? { role: 'Submitted', date: voucher.submittedDate } : null,
    voucher.approvedBy  ? { role: 'Approved',  date: voucher.approvedDate  } : null,
    voucher.postedBy    ? { role: 'Posted',    date: voucher.postedDate    } : null,
  ].filter(Boolean) as { role: string; date: string | null }[];

  return (
    <div style={{ fontFamily: 'Arial, Helvetica, sans-serif', fontSize: '11px', color: '#111827' }}>
      <VoucherLetterhead title="Petty Cash Voucher">
        <div style={{ fontSize: '16px', fontWeight: 700, color: '#1e3a5f', fontFamily: 'monospace' }}>
          {voucher.voucherNumber}
        </div>
        <div style={{ marginTop: '4px', fontSize: '11px', fontWeight: 600, color: '#374151' }}>
          {MOVEMENT_LABEL[voucher.movementType] ?? voucher.movementType}
          {voucher.isReversal && (
            <span style={{
              marginLeft: '6px', display: 'inline-block', padding: '1px 7px', borderRadius: '10px',
              fontSize: '9px', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
              background: '#fef3c7', color: '#92400e',
            }}>
              Reversal
            </span>
          )}
        </div>
      </VoucherLetterhead>

      {/* ── Document details ─────────────────────────────────────────────── */}
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 32px', marginBottom: '16px',
        padding: '12px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '6px',
      }}>
        <div>
          <div style={{ fontSize: '9px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>
            Petty Cash Float
          </div>
          <div style={{ fontSize: '12px', fontWeight: 700, color: '#111827' }}>{voucher.floatName}</div>
          <div style={{ fontSize: '10px', color: '#6b7280', fontFamily: 'monospace' }}>{voucher.floatCode}</div>
          <LabelValue label="Voucher Date" value={fmtDate(voucher.voucherDate)} />
          <LabelValue label="Posting Date" value={fmtDate(voucher.postingDate)} />
        </div>
        <div>
          {/* An itemised payment names its accounts in the breakdown below; repeating the first one
              here would read as though it were the only one (ML-13). */}
          {!hasExpenseLines && (
            <>
              <div style={{ fontSize: '9px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>
                {voucher.movementType === 'PAYMENT' ? 'Expense Account' : 'Funding Account'}
              </div>
              <div style={{ fontSize: '12px', fontWeight: 700, color: '#111827' }}>{voucher.counterGlAccountName}</div>
              <div style={{ fontSize: '10px', color: '#6b7280', fontFamily: 'monospace' }}>{voucher.counterGlAccountCode}</div>
            </>
          )}
          {voucher.reference && <LabelValue label="Reference" value={voucher.reference} />}
        </div>
      </div>

      {/* ── Expenses (ML-13) ─────────────────────────────────────────────
          What the money was actually spent on. A voucher showing only a total
          leaves the claimant and the approver to take it on trust. */}
      {hasExpenseLines && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '9px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>
            Expenses
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
            <thead>
              <tr style={{ background: '#f3f4f6' }}>
                <th style={{ textAlign: 'left',  padding: '5px 8px', width: '28px', color: '#6b7280' }}>#</th>
                <th style={{ textAlign: 'left',  padding: '5px 8px', color: '#6b7280' }}>Account</th>
                <th style={{ textAlign: 'left',  padding: '5px 8px', color: '#6b7280' }}>What it was for</th>
                <th style={{ textAlign: 'right', padding: '5px 8px', width: '90px', color: '#6b7280' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {(voucher.lines ?? []).map(line => (
                <tr key={line.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '5px 8px', color: '#6b7280' }}>{line.lineNumber}</td>
                  <td style={{ padding: '5px 8px', color: '#111827' }}>
                    <span style={{ fontFamily: 'monospace', color: '#6b7280', marginRight: '6px' }}>
                      {line.expenseGlAccountCode}
                    </span>
                    {line.expenseGlAccountName}
                  </td>
                  <td style={{ padding: '5px 8px', color: '#374151' }}>{line.description ?? ''}</td>
                  <td style={{ padding: '5px 8px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 700, color: '#111827' }}>
                    {fmtNum(line.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Amount ───────────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '14px 16px', marginBottom: '16px', background: '#eff6ff',
        border: '1px solid #bfdbfe', borderRadius: '6px',
      }}>
        <span style={{ fontSize: '11px', fontWeight: 600, color: '#1e3a5f' }}>Amount</span>
        <span style={{ fontSize: '20px', fontWeight: 800, color: '#1e3a5f', fontFamily: 'monospace' }}>
          {fmtNum(voucher.amount)} {voucher.currencyCode}
        </span>
      </div>

      {voucher.narration && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '9px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>
            Narration
          </div>
          <div style={{ fontSize: '11px', color: '#374151' }}>{voucher.narration}</div>
        </div>
      )}

      {/* ── Workflow trail ───────────────────────────────────────────────── */}
      {workflowRows.length > 0 && (
        <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '10px', marginTop: '8px' }}>
          {workflowRows.map(r => (
            <LabelValue key={r.role} label={r.role} value={fmtDate(r.date)} />
          ))}
        </div>
      )}

      {/* ── Signatures ───────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '48px', marginTop: '48px' }}>
        <div>
          <div style={{ borderTop: '1px solid #9ca3af', paddingTop: '4px', fontSize: '9px', color: '#6b7280' }}>
            Prepared By
          </div>
        </div>
        <div>
          <div style={{ borderTop: '1px solid #9ca3af', paddingTop: '4px', fontSize: '9px', color: '#6b7280' }}>
            Approved By
          </div>
        </div>
      </div>
    </div>
  );
};
